/**
 * ROUND8 §4: the wind arrow. A dog smells him further when the wind carries his scent to it and less far when it
 * carries it away (`noseReach`, src/sim/enemies/specials/dogNose.ts), and the standing orders tell him to "STAY
 * DOWNWIND" - an order he cannot follow without knowing which way the wind blows. The arrow is a small panel of
 * its own directly under the strip map, which is north-up exactly as the game camera is (fixed yaw), and it points
 * the way the wind BLOWS: the way his scent goes. It is labelled DOWNWIND, the orders' own word, so the arrow
 * teaches the word.
 *
 * Drawn whenever `def.wind` is not zero - it also pushes his parafoil and his pods on the way down - and kept under
 * Radio Silence, beside the map panel's "NO MAP" rather than in it: the wind is not a radio. It never changes during
 * a mission, so it is built once.
 */

import './windArrow.css';
import type { MissionDef } from '../../sim/types';
import { el } from './dom';

/** The arrow's label: the word the dogs' standing order uses. */
export const WIND_WORD = 'DOWNWIND';
/** The arrow turns in steps of this, degrees: sixteen points, as the sound pips' chevrons do. */
export const WIND_STEP_DEG = 22.5;

const SVG_NS = 'http://www.w3.org/2000/svg';
/** An arrow pointing right (east) on an 11 x 11 grid: a 3 px shaft and a head. */
const ARROW_PATH = 'M1 4h5V1l4 4.5L6 10V7H1z';

/**
 * How far to turn the right-pointing arrow, degrees clockwise on screen, for a wind of (x, z) m/s: 0 blows east,
 * 90 south (down the screen), 270 north (up it). Null when there is no wind to show.
 */
export function windTurn(wind: { readonly x: number; readonly z: number }): number | null {
  if (!Number.isFinite(wind.x) || !Number.isFinite(wind.z) || (wind.x === 0 && wind.z === 0)) return null;
  // Screen x is world +x (east) and screen y is world +z (south): atan2(z, x) is the clockwise screen angle.
  const deg = (Math.atan2(wind.z, wind.x) * 180) / Math.PI;
  const stepped = Math.round(deg / WIND_STEP_DEG) * WIND_STEP_DEG;
  return ((stepped % 360) + 360) % 360;
}

/** The arrow's panel for this mission - the arrow, then its word - or null on a still day (the training ground). */
export function windArrow(def: Pick<MissionDef, 'wind'>): HTMLElement | null {
  const turn = windTurn(def.wind);
  if (turn === null) return null;
  const box = el('div', 'hud-panel hud-wind');
  box.dataset['turn'] = String(turn);
  const icon = document.createElementNS(SVG_NS, 'svg');
  icon.setAttribute('class', 'hud-wind-arrow');
  icon.setAttribute('viewBox', '0 0 11 11');
  icon.style.transform = `rotate(${turn}deg)`;
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', ARROW_PATH);
  icon.append(path);
  box.append(icon, el('div', 'hud-wind-word', WIND_WORD));
  return box;
}

/**
 * The top-right corner: the strip map's panel, and the wind's under it in one column. On a still day the map
 * panel is returned as it is, so nothing about its placement changes.
 */
export function mapColumn(map: HTMLElement, def: Pick<MissionDef, 'wind'>): HTMLElement {
  const wind = windArrow(def);
  if (wind === null) return map;
  const column = el('div', 'hud-tr-col');
  column.append(map, wind);
  return column;
}
